import React, { useEffect, useState } from "react";
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  Image,
  ActivityIndicator,
  Platform,
  TextInput,
  TouchableOpacity,
  Switch
} from "react-native";
import Glob from "src/globalConstants";
import Database from "src/backend/database";
import Analytics from "src/backend/analytics";
import Util from "src/utility";
import NavBar from "src/components/navBar";
import Button from "src/components/Button";
import BottomSheetModal from "src/components/BottomSheetModal";
import { ImageZoom } from "@likashefqet/react-native-image-zoom";
import FontAwesome5 from "react-native-vector-icons/FontAwesome5";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import Rex from "src/globalState";

const { height, width } = Glob.get("dimensions");
const STRIPE_DASHBOARD_URL = "https://dashboard.stripe.com/dashboard";
const STRIPE_FEES_URL = "https://stripe.com/pricing";
// Define example amounts in dollars
const EXAMPLE_AMOUNTS = [
  { value: 10, label: "T-Shirt", icon: "tshirt" },
  { value: 100, label: "Field Trip", icon: "bus" },
  { value: 1000, label: "Tuition", icon: "graduation-cap" }
];
const STRIPE_FEE_PERCENT_CARD = 2.9;
const STRIPE_FEE_CENTS_CARD = 30;
const STRIPE_FEE_PERCENT_ACH = 0.8;
const STRIPE_FEE_MAX_CENTS_ACH = 500;
const MAX_FEE_PERCENT = 5.0; // Maximum 5% service fee
// Primary color for brand consistency
const primaryColor = Glob.get("onespotColor");

export default function CommerceSettings({ navigation }) {
  const [accountLinkURL, setAccountLinkURL] = useState(null);
  const [loadedCommerceConfig, setLoadedCommerceConfig] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [commerceConfig, setCommerceConfig] = useState({});
  const [transactionFees, setTransactionFees] = useState({});
  const [loadingFees, setLoadingFees] = useState(false);
  const [stripeAccount, setStripeAccount] = useState(null);
  const [showFeeConfig, setShowFeeConfig] = useState(false);
  const [selectedExampleIndex, setSelectedExampleIndex] = useState(1); // Default to Field Trip ($100)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("card"); // Default to card payment
  const [simulatorExpanded, setSimulatorExpanded] = useState(true); // Track if the Fee Simulator is expanded

  // Local state for form values
  const [serviceFeeEnabled, setServiceFeeEnabled] = useState(false);
  const [serviceFeePercent, setServiceFeePercent] = useState("0.0");
  const [serviceFeeThreshold, setServiceFeeThreshold] = useState("0");
  const [achDiscountEnabled, setAchDiscountEnabled] = useState(false);
  const [achDiscountPercent, setAchDiscountPercent] = useState("0.0");
  const [achDiscountThreshold, setAchDiscountThreshold] = useState("0");

  const stripeAccountIsConfigured =
    stripeAccount?.charges_enabled && stripeAccount?.payouts_enabled;

  useEffect(() => {
    Analytics.logEvent("view_commerceSettings");
    Database.subscribeToCommerce(async (data) => {
      const { config = {} } = data || {};
      if (config?.stripeConnect?.accountID) {
        const account = await Database.fetchStripeConnectedAccount();
        setStripeAccount(account);
        // Initial fee calculation will happen in fetchTransactionFees
      }

      setCommerceConfig(config);

      // Initialize form values from config
      const { serviceFee = {}, achDiscount = {} } = config || {};
      setServiceFeeEnabled(serviceFee?.enabled || false);
      setServiceFeePercent(((serviceFee?.percent || 0) * 100).toFixed(1));
      setServiceFeeThreshold(String(serviceFee?.thresholdDollarValue || 0));
      setAchDiscountEnabled(achDiscount?.enabled || false);
      setAchDiscountPercent(((achDiscount?.percent || 0) * 100).toFixed(1));
      setAchDiscountThreshold(String(achDiscount?.thresholdDollarValue || 0));

      setLoadedCommerceConfig(true);
      setIsSavingSettings(false);
      setShowFeeConfig(serviceFee?.enabled || achDiscount?.enabled);

      // Fetch transaction fees for the selected example
      if (config?.stripeConnect?.accountID) {
        fetchTransactionFees(EXAMPLE_AMOUNTS[selectedExampleIndex].value);
      }
    });
    // cleanup called when component is unmounting
    return () => {
      Database.unsubscribeFromCommerce();
    };
  }, []);

  // When selected example changes, fetch transaction fees if needed
  useEffect(() => {
    if (loadedCommerceConfig && stripeConnect?.accountID) {
      const currentAmount = EXAMPLE_AMOUNTS[selectedExampleIndex].value;
      if (!transactionFees[currentAmount]) {
        fetchTransactionFees(currentAmount);
      }
    }
  }, [selectedExampleIndex, loadedCommerceConfig]);

  useEffect(() => {
    const customerPays = calculateCustomerTotal(
      EXAMPLE_AMOUNTS[selectedExampleIndex].value,
      selectedPaymentMethod
    );
    fetchTransactionFees(customerPays);
  }, [
    selectedExampleIndex,
    selectedPaymentMethod,
    serviceFeeEnabled,
    serviceFeePercent,
    serviceFeeThreshold,
    achDiscountEnabled,
    achDiscountPercent,
    achDiscountThreshold
  ]);

  const { stripeConnect = {}, onespotFees = {} } = commerceConfig || {};
  const { accountID = null } = stripeConnect || {};

  const configureStripeConnect = async () => {
    Analytics.logEvent("touch_commerceSettings_stripeConfiguration");
    // If there's already an account ID, we don't need to configure
    let finalAccountID = accountID;
    if (!finalAccountID) {
      setIsLoadingButton(true);
      const { account, error } = await Database.createStripeConnectedAccount();
      Analytics.logEvent("action_commerceSettings_stripeAccountCreated", {
        account,
        error
      });
      finalAccountID = account;
      setIsLoadingButton(false);
    }

    if (finalAccountID) {
      Analytics.logEvent("action_commerceSettings_stripeAccountFetched", {
        accountID: finalAccountID
      });
      setIsLoadingButton(true);
      const { url, error } = await Database.createStripeConnectedAccountLink();
      Analytics.logEvent("action_commerceSettings_stripeAccountLinkFetched", {
        url,
        error
      });
      setIsLoadingButton(false);
      if (Platform.OS === "web") Util.openURL(url);
      else setAccountLinkURL(url);
    }
  };

  const onPressOffsetFees = () => {
    const { overallPercent = 0, card: { percent = 0 } = {} } = onespotFees;
    let onespotPercent = percent;
    if (overallPercent > 0) onespotPercent = overallPercent;
    // If Onespot fee is 0.3%, combinedFeePercent will be 4%
    const combinedFeePercent =
      parseFloat((onespotPercent * 100).toFixed(2)) + 3.7; // Slightly more than 2.9 + 0.7
    setShowFeeConfig(true);
    setServiceFeeEnabled(true);
    setServiceFeePercent(`${combinedFeePercent}`);
    setServiceFeeThreshold("0");
    setAchDiscountEnabled(true);
    setAchDiscountPercent("3");
    setAchDiscountThreshold("0");
  };

  // Save fee settings to database
  const saveSettings = async () => {
    setIsSavingSettings(true);

    try {
      await Database.stripeConnectedAccountUpdateServiceFeesAndDiscounts({
        achDiscount: {
          enabled: achDiscountEnabled,
          percent: parseFloat(achDiscountPercent) / 100, // Convert to decimal
          thresholdDollarValue: parseFloat(achDiscountThreshold)
        },
        serviceFee: {
          enabled: serviceFeeEnabled,
          percent: parseFloat(serviceFeePercent) / 100, // Convert to decimal
          thresholdDollarValue: parseFloat(serviceFeeThreshold)
        }
      });
      Analytics.logEvent("touch_commerceSettings_savedFeeStructure", {
        serviceFeeEnabled,
        serviceFeePercent,
        serviceFeeThreshold,
        achDiscountEnabled,
        achDiscountPercent,
        achDiscountThreshold
      });

      // The subscription will update the local state
    } catch (error) {
      console.error("Failed to update commerce settings:", error);
      setIsSavingSettings(false);
      Util.alert("Error", "Failed to save settings. Please try again.");
    }
  };

  const openAppInBrowser = () => {
    Database.fetchPrimaryMetaApp().then((metaApp) => {
      if (metaApp?.webLink) {
        Util.alert(
          "Open on your computer",
          "Open the web version of your app in your phone's browser. Then, copy the page link and send it to your computer so you can navigate back to this commerce page from there.",
          [
            {
              text: "Cancel",
              onPress: () => {},
              style: "cancel"
            },
            {
              text: "Open in Browser",
              onPress: () => Util.openURL(metaApp.webLink)
            }
          ]
        );
      } else Util.alert("No web version", "Sorry, no web version available.");
    });
  };

  // Fetch transaction fees for a specific amount
  const fetchTransactionFees = async (amountDollars) => {
    if (!stripeConnect?.accountID) return;

    setLoadingFees(true);
    try {
      const fees = await Database.stripeConnectedAccountCalculateTransactionFees(
        amountDollars * 100 // Convert to cents
      );
      setTransactionFees((prevFees) => ({
        ...prevFees,
        [amountDollars]: fees
      }));
    } catch (error) {
      console.error("Failed to calculate transaction fees:", error);
    } finally {
      setLoadingFees(false);
    }
  };

  // Handle example selection change
  const handleExampleChange = (index) => {
    setSelectedExampleIndex(index);
    const amount = EXAMPLE_AMOUNTS[index].value;
    // Only fetch new fees if we don't have them cached
    if (!transactionFees[amount]) {
      fetchTransactionFees(amount);
    }
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  // Format cents to dollars
  const centsToDisplayDollars = (cents) => {
    return formatCurrency(cents / 100);
  };

  // Calculate customer total based on payment method
  const calculateCustomerTotal = (baseAmount, method) => {
    if (!serviceFeeEnabled && !achDiscountEnabled) {
      return baseAmount;
    }

    const serviceFeeAmount =
      serviceFeeEnabled && baseAmount > (parseFloat(serviceFeeThreshold) || 0)
        ? baseAmount * (parseFloat(serviceFeePercent) / 100)
        : 0;

    const achDiscountAmount =
      achDiscountEnabled && baseAmount > (parseFloat(achDiscountThreshold) || 0)
        ? baseAmount * (parseFloat(achDiscountPercent) / 100)
        : 0;

    const totalWithServiceFee = baseAmount + serviceFeeAmount;
    const totalWithACHDiscount = totalWithServiceFee - achDiscountAmount;

    if (method === "card") {
      return totalWithServiceFee;
    }
    if (method === "ach") {
      return totalWithACHDiscount;
    }
    return baseAmount;
  };

  // Calculate the amount the school receives
  const calculateSchoolAmount = (baseAmount, paymentMethod, processingFees) => {
    // Start with base amount
    let schoolAmount = baseAmount;

    // Add service fee if enabled and applicable (service fee goes to the school)
    if (
      serviceFeeEnabled &&
      baseAmount > (parseFloat(serviceFeeThreshold) || 0)
    ) {
      schoolAmount += baseAmount * (parseFloat(serviceFeePercent) / 100);
    }

    // Subtract ACH discount if applicable (discount reduces what school receives)
    if (
      paymentMethod === "ach" &&
      achDiscountEnabled &&
      baseAmount > (parseFloat(achDiscountThreshold) || 0)
    ) {
      schoolAmount -= baseAmount * (parseFloat(achDiscountPercent) / 100);
    }

    // Subtract processing fees
    schoolAmount -= processingFees;

    return schoolAmount;
  };

  if (!loadedCommerceConfig)
    return (
      <View style={styles.pageContent}>
        <NavBar
          navigation={navigation}
          text="Onespot Payments"
          backgroundColor={primaryColor}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={primaryColor} />
        </View>
      </View>
    );

  if (!stripeAccountIsConfigured) {
    const imageWidth = Math.min(width, height * 0.6);
    const imageHeight = imageWidth * (1351 / 2000); // exact dimensions of image
    return (
      <View style={[styles.pageContent, { backgroundColor: "white" }]}>
        <NavBar
          navigation={navigation}
          text="Onespot Payments"
          backgroundColor={primaryColor}
        />
        <ScrollView
          style={styles.scrollView}
          scrollIndicatorInsets={{ right: 1 }}
        >
          <View style={styles.contentContainerStarting}>
            {Platform.OS === "web" ? (
              <Image
                source={Glob.get("commercePaymentsSchools")}
                style={[
                  styles.heroImage,
                  { height: imageHeight, width: imageWidth }
                ]}
              />
            ) : (
              <ImageZoom
                source={Glob.get("commercePaymentsSchools")}
                style={[
                  styles.heroImage,
                  { height: imageHeight, width: imageWidth }
                ]}
              />
            )}
            <Text style={styles.heading}>Ready to start taking payments?</Text>
            <Text style={styles.bodyText}>
              Onespot Payments makes it super easy to accept payments online.
              Our tools help you add a secure payment system to your app,
              protecting sensitive information and offering flexible payment
              options.
            </Text>
            <Text style={styles.bodyText}>
              Streamline transactions, reduce administrative burden, and provide
              an elegant payments solution for your whole community, all in one
              spot.
            </Text>

            <Button
              text="Watch an overview"
              icon="6e261355-6ba8-44d0-9284-1fb8a304abd2"
              style={styles.button}
              color={primaryColor}
              outline
              onPress={() => {
                navigation.push("webNav", {
                  title: "Onespot Payments Overview",
                  url: "https://www.youtube.com/watch?v=wPI1LA_8eVQ"
                });
              }}
            />

            <Text style={[styles.bodyText, { marginTop: 20 }]}>
              {!accountID
                ? "Onespot uses Stripe for enterprise-grade, secure payment processing. When you're ready to get started, configure"
                : "Finish configuring"}{" "}
              your Stripe account to start accepting payments:
            </Text>

            {isLoadingButton ? (
              <ActivityIndicator
                size="large"
                style={styles.loadingIndicator}
                color={primaryColor}
              />
            ) : (
              <>
                {!accountID ? (
                  <Button
                    text="Get started"
                    style={styles.primaryButton}
                    textStyle={styles.primaryButtonText}
                    color={primaryColor}
                    onPress={configureStripeConnect}
                    showNotification
                  />
                ) : (
                  <Button
                    text="Finish setting up account"
                    style={styles.primaryButton}
                    textStyle={styles.primaryButtonText}
                    color={primaryColor}
                    onPress={configureStripeConnect}
                    showNotification
                  />
                )}

                <Button
                  text="Help docs"
                  icon="lightbulb"
                  style={styles.button}
                  color={primaryColor}
                  outline
                  onPress={() => {
                    navigation.push("webNav", {
                      title: "Setting up Onespot Payments",
                      url:
                        "https://onespot.helpscoutdocs.com/article/49-onespot-payments-initial-set-up"
                    });
                  }}
                />
                <Button
                  text="Talk with us"
                  icon="b7c8058a-e694-4ac5-ab9a-daa6b990a91c"
                  style={styles.button}
                  color={primaryColor}
                  outline
                  onPress={() => navigation.push("help", { isSchool: true })}
                />

                {Platform.OS !== "web" && (
                  <>
                    <Text style={[styles.bodyText, { marginTop: 20 }]}>
                      Note: You can configure your account in the{" "}
                      {Platform.OS === "ios" ? "iOS" : "Android"} app if you
                      want, but it's a bit easier to do on a computer:
                    </Text>
                    <Button
                      text="Open app on computer"
                      icon="3f65aaa0-9297-4b25-9b23-e8772ae70d24"
                      style={styles.secondaryButton}
                      outline
                      color={primaryColor}
                      onPress={openAppInBrowser}
                    />
                  </>
                )}
              </>
            )}
          </View>
        </ScrollView>
        {!!accountLinkURL && (
          <BottomSheetModal
            onClose={() => setAccountLinkURL(null)}
            url={accountLinkURL}
          />
        )}
      </View>
    );
  }

  const shouldShowFeeConfig =
    showFeeConfig || serviceFeeEnabled || achDiscountEnabled;

  const {
    overallPercent = 0,
    card: { percent = 0, centsPerTransaction = 0 } = {},
    ach: {
      percentCombinedWithStripe = 0,
      maxFeeCentsCombinedWithStripe = 0
    } = {}
  } = onespotFees || {};
  const overallOnespotPercent = parseFloat((overallPercent * 100).toFixed(1));
  const combinedACHPercent =
    percentCombinedWithStripe > STRIPE_FEE_PERCENT_ACH / 100
      ? percentCombinedWithStripe
      : STRIPE_FEE_PERCENT_ACH / 100;
  const onespotACHFeePercent = combinedACHPercent
    ? combinedACHPercent - STRIPE_FEE_PERCENT_ACH / 100
    : 0;
  const combinedACHMaxCents =
    maxFeeCentsCombinedWithStripe > STRIPE_FEE_MAX_CENTS_ACH
      ? maxFeeCentsCombinedWithStripe
      : STRIPE_FEE_MAX_CENTS_ACH;
  const onespotCardFeePercent = parseFloat((percent * 100).toFixed(1));
  const combinedCardFeePercent =
    STRIPE_FEE_PERCENT_CARD + onespotCardFeePercent;

  const exampleCustomerPays = calculateCustomerTotal(
    EXAMPLE_AMOUNTS[selectedExampleIndex].value,
    selectedPaymentMethod
  );

  return (
    <View style={styles.pageContent}>
      <NavBar
        navigation={navigation}
        text="Onespot Payments"
        backgroundColor={primaryColor}
      />
      <ScrollView
        style={styles.scrollView}
        scrollIndicatorInsets={{ right: 1 }}
      >
        <View style={styles.contentContainer}>
          <View style={styles.header}>
            <FontAwesome5
              name="check-circle"
              size={32}
              color={primaryColor}
              style={styles.iconSuccess}
            />
            <Text style={styles.headerTitle}>Your account is set up</Text>
          </View>

          <Text style={styles.subheadingText}>
            View your customers, orders, payouts, and more in your Stripe
            dashboard.
          </Text>

          <TouchableOpacity
            style={styles.stripeDashboardButton}
            onPress={() => {
              navigation.push("webNav", {
                title: "Stripe Dashboard",
                url: STRIPE_DASHBOARD_URL
              });
            }}
          >
            <Text style={styles.stripeDashboardButtonText}>
              Stripe Dashboard
            </Text>
            <MaterialIcons name="open-in-new" size={18} color="white" />
          </TouchableOpacity>

          {/* Account Info Card */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <FontAwesome5
                name="user"
                size={18}
                color={primaryColor}
                style={styles.cardHeaderIcon}
              />
              <Text style={styles.cardHeaderTitle}>Account Info</Text>
            </View>

            <View style={styles.cardBody}>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Name</Text>
                <Text style={styles.infoValue}>
                  {stripeAccount?.business_profile?.name}
                </Text>
              </View>

              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Email</Text>
                <Text style={styles.infoValue}>{stripeAccount?.email}</Text>
              </View>

              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Location</Text>
                <Text style={styles.infoValue}>
                  {stripeAccount?.business_profile?.support_address?.city},{" "}
                  {stripeAccount?.business_profile?.support_address?.state}
                </Text>
              </View>
            </View>

            <TouchableOpacity
              style={styles.cardAction}
              onPress={configureStripeConnect}
            >
              <Text style={styles.cardActionText}>Edit Configuration</Text>
              <MaterialIcons name="edit" size={16} color={primaryColor} />
            </TouchableOpacity>
          </View>

          {/* Fee Structure Card */}
          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <FontAwesome5
                name="money-bill-wave"
                size={18}
                color={primaryColor}
                style={styles.cardHeaderIcon}
              />
              <Text style={styles.cardHeaderTitle}>Fee Structure</Text>
            </View>

            <View style={styles.cardBody}>
              {/* Platform Fee Information */}
              <View>
                {overallOnespotPercent > 0 ? (
                  <View style={styles.feeMethodSection}>
                    <View style={styles.platformFeeRow}>
                      <Text style={styles.platformFeeLabel}>
                        Stripe Fee (Cards):
                      </Text>
                      <Text style={styles.platformFeeValue}>
                        {STRIPE_FEE_PERCENT_CARD}% + {STRIPE_FEE_CENTS_CARD}¢
                      </Text>
                    </View>

                    <View style={styles.platformFeeRow}>
                      <Text style={styles.platformFeeLabel}>
                        Stripe Fee (ACH):
                      </Text>
                      <Text style={styles.platformFeeValue}>
                        {STRIPE_FEE_PERCENT_ACH}% (max $
                        {STRIPE_FEE_MAX_CENTS_ACH / 100})
                      </Text>
                    </View>

                    <View style={styles.platformFeeRow}>
                      <Text style={styles.platformFeeLabel}>Onespot Fee:</Text>
                      <Text style={styles.platformFeeValue}>
                        {overallOnespotPercent}%
                      </Text>
                    </View>
                  </View>
                ) : (
                  <>
                    <View style={styles.feeMethodSection}>
                      <View style={styles.feeMethodHeaderRow}>
                        <View style={styles.feeMethodHeader}>
                          <FontAwesome5
                            name="credit-card"
                            size={14}
                            color="#555"
                            style={styles.feeMethodIcon}
                          />
                          <Text style={styles.feeMethodTitle}>
                            Overall Card Fee
                          </Text>
                        </View>
                        <Text style={styles.feeMethodTitle}>
                          {combinedCardFeePercent}% +{" "}
                          {STRIPE_FEE_CENTS_CARD + centsPerTransaction}¢
                        </Text>
                      </View>

                      <View style={styles.platformFeeRow}>
                        <Text style={styles.platformFeeLabel}>Stripe Fee:</Text>
                        <Text style={styles.platformFeeValue}>
                          {STRIPE_FEE_PERCENT_CARD}% + {STRIPE_FEE_CENTS_CARD}¢
                        </Text>
                      </View>

                      <View style={styles.platformFeeRow}>
                        <Text style={styles.platformFeeLabel}>
                          Onespot Fee:
                        </Text>
                        <Text style={styles.platformFeeValue}>
                          {onespotCardFeePercent}%
                          {centsPerTransaction > 0
                            ? ` + ${centsPerTransaction}¢`
                            : ""}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.feeMethodSection}>
                      <View style={styles.feeMethodHeaderRow}>
                        <View style={styles.feeMethodHeader}>
                          <FontAwesome5
                            name="university"
                            size={14}
                            color="#555"
                            style={styles.feeMethodIcon}
                          />
                          <Text style={styles.feeMethodTitle}>
                            Overall ACH Fee
                          </Text>
                        </View>
                        <Text style={styles.feeMethodTitle}>
                          {(combinedACHPercent * 100).toFixed(1)}% (max $
                          {combinedACHMaxCents / 100})
                        </Text>
                      </View>

                      <View style={styles.platformFeeRow}>
                        <Text style={styles.platformFeeLabel}>Stripe Fee:</Text>
                        <Text style={styles.platformFeeValue}>
                          {STRIPE_FEE_PERCENT_ACH}%
                        </Text>
                      </View>

                      <View style={styles.platformFeeRow}>
                        <Text style={styles.platformFeeLabel}>
                          Onespot Fee:
                        </Text>
                        <Text style={styles.platformFeeValue}>
                          {`${parseFloat(
                            (onespotACHFeePercent * 100).toFixed(1)
                          )}%`}
                        </Text>
                      </View>
                    </View>
                  </>
                )}

                <View style={styles.feeMethodSection}>
                  <Text style={[styles.platformFeeLabel, { fontSize: 12 }]}>
                    Stripe may charge your account additional fees related to
                    product usage, such as a 0.4% fee for Invoicing (one-time
                    charges) or a 0.7% fee for Billing (recurring charges).{" "}
                    {Rex.getConfig()?.commerceBillingEnabled &&
                    overallOnespotPercent > 0 ? (
                      <>
                        <Text style={{ fontWeight: "600" }}>
                          {`We recommend adding a ${0.7 +
                            overallOnespotPercent +
                            3}% Sevice Fee and a 3% ACH Discount in order to offset 100% of your percentage-based fees.`}
                        </Text>
                        <Text style={{ fontSize: 12 }}>
                          {" "}
                          Disclaimer: This is not legal or financial advice.
                        </Text>
                      </>
                    ) : (
                      ""
                    )}
                  </Text>
                </View>

                <TouchableOpacity
                  style={[
                    styles.platformFeeLink,
                    { marginTop: 0, marginBottom: 0 }
                  ]}
                  onPress={() => {
                    navigation.push("webNav", {
                      title: "Stripe Fees",
                      url: STRIPE_FEES_URL
                    });
                  }}
                >
                  <Text style={styles.platformFeeLinkText}>
                    View detailed Stripe fees
                  </Text>
                  <MaterialIcons
                    name="open-in-new"
                    size={14}
                    color={primaryColor}
                  />
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.platformFeeLink}
                  onPress={() => {
                    navigation.push("webNav", {
                      title: "Stripe Fees",
                      url:
                        "https://onespot.helpscoutdocs.com/article/69-stripe-onespot-payments-fee-structure"
                    });
                  }}
                >
                  <Text style={styles.platformFeeLinkText}>
                    View our breakdown of Stripe fees
                  </Text>
                  <MaterialIcons
                    name="open-in-new"
                    size={14}
                    color={primaryColor}
                  />
                </TouchableOpacity>
              </View>
              {!!shouldShowFeeConfig && (
                <>
                  <View style={styles.compactFeeInfoContainer}>
                    <FontAwesome5
                      name="percentage"
                      size={20}
                      color={primaryColor}
                      style={styles.compactFeeIcon}
                    />
                    <View style={styles.compactFeeTextContainer}>
                      <Text style={styles.compactFeeTitle}>
                        Offset Your Fees
                      </Text>
                      <Text style={styles.compactFeeDescription}>
                        Cover your payment processing costs by adding a small
                        service fee to all payments and/or offering a discount
                        for ACH bank transfers to incentivize lower-fee
                        payments. Note that these fees/discounts only apply to
                        billing & invoices, not to self-checkout.
                      </Text>
                    </View>
                  </View>
                  {/* Service Fee Section */}
                  <View style={styles.feeSection}>
                    <View style={styles.feeSectionHeader}>
                      <View style={styles.feeSectionTitleContainer}>
                        <FontAwesome5
                          name="percentage"
                          size={14}
                          color="#555"
                          style={styles.feeSectionIcon}
                        />
                        <Text style={styles.feeSectionTitle}>
                          Add Service Fee
                        </Text>
                      </View>
                      <Switch
                        value={serviceFeeEnabled}
                        onValueChange={setServiceFeeEnabled}
                        trackColor={{
                          false: "#e0e0e0",
                          true: `${primaryColor}80`
                        }}
                        thumbColor={
                          serviceFeeEnabled ? primaryColor : "#f4f3f4"
                        }
                      />
                    </View>

                    {serviceFeeEnabled && (
                      <View style={styles.feeInputContainer}>
                        <View style={styles.feeInputRow}>
                          <Text style={styles.feeInputLabel}>
                            Fee Percentage:
                          </Text>
                          <View style={styles.percentageInputContainer}>
                            <TextInput
                              style={styles.percentageInput}
                              value={serviceFeePercent}
                              onChangeText={(text) => {
                                const value = parseFloat(text);
                                if (
                                  !Number.isNaN(value) &&
                                  value <= MAX_FEE_PERCENT
                                ) {
                                  setServiceFeePercent(text);
                                } else if (text === "") {
                                  setServiceFeePercent("");
                                }
                              }}
                              keyboardType="numeric"
                              placeholder="0.0"
                              maxLength={4}
                            />
                            <Text style={styles.percentageSymbol}>%</Text>
                          </View>
                        </View>

                        <View style={styles.feeInputRow}>
                          <Text style={styles.feeInputLabel}>
                            Only apply to charges above:
                          </Text>
                          <View style={styles.thresholdInputContainer}>
                            <Text style={styles.currencySymbol}>$</Text>
                            <TextInput
                              style={styles.thresholdInput}
                              value={serviceFeeThreshold}
                              onChangeText={setServiceFeeThreshold}
                              keyboardType="numeric"
                              placeholder="0.00"
                            />
                          </View>
                        </View>

                        <Text style={styles.feeDescription}>
                          Add a service fee to cover processing costs. This will
                          be clearly shown to customers as a separate charge.
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* ACH Discount Section */}
                  <View style={styles.feeSection}>
                    <View style={styles.feeSectionHeader}>
                      <View style={styles.feeSectionTitleContainer}>
                        <FontAwesome5
                          name="university"
                          size={14}
                          color="#555"
                          style={styles.feeSectionIcon}
                        />
                        <Text style={styles.feeSectionTitle}>
                          Give ACH Discount
                        </Text>
                      </View>
                      <Switch
                        value={achDiscountEnabled}
                        onValueChange={setAchDiscountEnabled}
                        trackColor={{
                          false: "#e0e0e0",
                          true: `${primaryColor}80`
                        }}
                        thumbColor={
                          achDiscountEnabled ? primaryColor : "#f4f3f4"
                        }
                      />
                    </View>

                    {achDiscountEnabled && (
                      <View style={styles.feeInputContainer}>
                        <View style={styles.feeInputRow}>
                          <Text style={styles.feeInputLabel}>
                            Discount Percentage:
                          </Text>
                          <View style={styles.percentageInputContainer}>
                            <TextInput
                              style={styles.percentageInput}
                              value={achDiscountPercent}
                              onChangeText={(text) => {
                                const value = parseFloat(text);
                                if (
                                  !Number.isNaN(value) &&
                                  value <= MAX_FEE_PERCENT
                                ) {
                                  setAchDiscountPercent(text);
                                } else if (text === "") {
                                  setAchDiscountPercent("");
                                }
                              }}
                              keyboardType="numeric"
                              placeholder="0.0"
                              maxLength={4}
                            />
                            <Text style={styles.percentageSymbol}>%</Text>
                          </View>
                        </View>

                        <View style={styles.feeInputRow}>
                          <Text style={styles.feeInputLabel}>
                            Only apply to charges above:
                          </Text>
                          <View style={styles.thresholdInputContainer}>
                            <Text style={styles.currencySymbol}>$</Text>
                            <TextInput
                              style={styles.thresholdInput}
                              value={achDiscountThreshold}
                              onChangeText={setAchDiscountThreshold}
                              keyboardType="numeric"
                              placeholder="0.00"
                            />
                          </View>
                        </View>

                        <Text style={styles.feeDescription}>
                          Offer a discount to customers who pay using bank
                          transfers (ACH) instead of credit cards.
                        </Text>
                      </View>
                    )}
                  </View>
                </>
              )}
              {Rex.getConfig()?.commerceBillingEnabled && (
                <>
                  {showFeeConfig || serviceFeeEnabled || achDiscountEnabled ? (
                    <TouchableOpacity
                      style={[
                        styles.saveButton,
                        isSavingSettings && styles.saveButtonDisabled
                      ]}
                      onPress={saveSettings}
                      disabled={isSavingSettings}
                    >
                      {isSavingSettings ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                      ) : (
                        <>
                          <MaterialIcons
                            name="save"
                            size={18}
                            color="#ffffff"
                          />
                          <Text style={styles.saveButtonText}>
                            Save Settings
                          </Text>
                        </>
                      )}
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity
                      style={styles.setupFeeButton}
                      onPress={onPressOffsetFees}
                    >
                      <FontAwesome5
                        name="percentage"
                        size={18}
                        color="#ffffff"
                      />
                      <Text style={styles.setupFeeButtonText}>
                        Offset Your Fees
                      </Text>
                    </TouchableOpacity>
                  )}
                </>
              )}

              {/* Example Section */}
              <View style={styles.exampleSection}>
                <TouchableOpacity
                  style={[
                    styles.exampleHeader,
                    simulatorExpanded && styles.exampleHeaderExpanded
                  ]}
                  onPress={() => setSimulatorExpanded(!simulatorExpanded)}
                  activeOpacity={0.7}
                >
                  <FontAwesome5
                    name="info-circle"
                    size={16}
                    color={primaryColor}
                  />
                  <View style={styles.headerTextContainer}>
                    <Text style={styles.exampleHeaderText}>Fee Simulator</Text>
                    <Text style={styles.exampleHeaderSubtext}>
                      A transparent breakdown of fees
                    </Text>
                  </View>
                  <MaterialIcons
                    name={
                      simulatorExpanded
                        ? "keyboard-arrow-up"
                        : "keyboard-arrow-down"
                    }
                    size={24}
                    color="#555"
                    style={styles.expandCollapseIcon}
                  />
                </TouchableOpacity>

                {simulatorExpanded && (
                  <>
                    {/* Example Selector */}
                    <View style={styles.exampleSelectorContainer}>
                      <Text style={styles.exampleSelectorLabel}>
                        Select an example transaction:
                      </Text>
                      <View style={styles.exampleSelector}>
                        {EXAMPLE_AMOUNTS.map((example, index) => (
                          <TouchableOpacity
                            key={index}
                            style={[
                              styles.exampleButton,
                              selectedExampleIndex === index &&
                                styles.exampleButtonSelected
                            ]}
                            onPress={() => handleExampleChange(index)}
                            activeOpacity={0.7}
                          >
                            <FontAwesome5
                              name={example.icon}
                              size={16}
                              color={
                                selectedExampleIndex === index
                                  ? primaryColor
                                  : "#777"
                              }
                              style={styles.exampleButtonIcon}
                            />
                            <Text
                              style={[
                                styles.exampleButtonText,
                                selectedExampleIndex === index &&
                                  styles.exampleButtonTextSelected
                              ]}
                            >
                              ${example.value}
                            </Text>
                            <Text
                              style={[
                                styles.exampleButtonSubtext,
                                selectedExampleIndex === index &&
                                  styles.exampleButtonSubtextSelected
                              ]}
                            >
                              {example.label}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>

                    {/* Payment Method Toggle */}
                    <View style={styles.paymentMethodContainer}>
                      <Text style={styles.exampleSelectorLabel}>
                        Select a payment method:
                      </Text>
                      <View style={styles.paymentMethodToggle}>
                        <TouchableOpacity
                          style={[
                            styles.paymentMethodButton,
                            selectedPaymentMethod === "card" &&
                              styles.paymentMethodButtonSelected
                          ]}
                          onPress={() => setSelectedPaymentMethod("card")}
                        >
                          <FontAwesome5
                            name="credit-card"
                            size={16}
                            color={
                              selectedPaymentMethod === "card"
                                ? primaryColor
                                : "#777"
                            }
                            style={styles.paymentMethodButtonIcon}
                          />
                          <Text
                            style={[
                              styles.paymentMethodButtonText,
                              selectedPaymentMethod === "card" &&
                                styles.paymentMethodButtonTextSelected
                            ]}
                          >
                            Card
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[
                            styles.paymentMethodButton,
                            selectedPaymentMethod === "ach" &&
                              styles.paymentMethodButtonSelected
                          ]}
                          onPress={() => setSelectedPaymentMethod("ach")}
                        >
                          <FontAwesome5
                            name="university"
                            size={16}
                            color={
                              selectedPaymentMethod === "ach"
                                ? primaryColor
                                : "#777"
                            }
                            style={styles.paymentMethodButtonIcon}
                          />
                          <Text
                            style={[
                              styles.paymentMethodButtonText,
                              selectedPaymentMethod === "ach" &&
                                styles.paymentMethodButtonTextSelected
                            ]}
                          >
                            ACH
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>

                    {/* Transaction Details */}
                    <Text style={styles.exampleSelectorLabel}>
                      Fee breakdown:
                    </Text>
                    <View style={styles.transactionDetails}>
                      {loadingFees ? (
                        <ActivityIndicator
                          size="small"
                          color={primaryColor}
                          style={styles.feeLoader}
                        />
                      ) : (
                        <>
                          <View style={styles.transactionRow}>
                            <Text style={styles.transactionLabel}>
                              Base Amount:
                            </Text>
                            <Text style={styles.transactionValue}>
                              $
                              {EXAMPLE_AMOUNTS[
                                selectedExampleIndex
                              ].value.toFixed(2)}
                            </Text>
                          </View>

                          {transactionFees[
                            EXAMPLE_AMOUNTS[selectedExampleIndex].value
                          ] && (
                            <>
                              {/* Service fee line item (if applicable) */}
                              {serviceFeeEnabled &&
                                EXAMPLE_AMOUNTS[selectedExampleIndex].value >
                                  (parseFloat(serviceFeeThreshold) || 0) && (
                                  <View
                                    style={[
                                      styles.transactionRow,
                                      styles.serviceFeeRow
                                    ]}
                                  >
                                    <Text style={styles.transactionLabel}>
                                      <FontAwesome5
                                        name="plus"
                                        size={12}
                                        color="#e67e22"
                                        style={styles.rowIcon}
                                      />{" "}
                                      Service Fee:
                                    </Text>
                                    <Text
                                      style={[
                                        styles.transactionValue,
                                        styles.serviceFeeValue
                                      ]}
                                    >
                                      {formatCurrency(
                                        EXAMPLE_AMOUNTS[selectedExampleIndex]
                                          .value *
                                          (parseFloat(serviceFeePercent) / 100)
                                      )}
                                    </Text>
                                  </View>
                                )}

                              {/* ACH discount line item (if applicable) */}
                              {achDiscountEnabled &&
                                selectedPaymentMethod === "ach" &&
                                EXAMPLE_AMOUNTS[selectedExampleIndex].value >
                                  (parseFloat(achDiscountThreshold) || 0) && (
                                  <View
                                    style={[
                                      styles.transactionRow,
                                      styles.discountRow
                                    ]}
                                  >
                                    <Text style={styles.transactionLabel}>
                                      <FontAwesome5
                                        name="tag"
                                        size={12}
                                        color="#27ae60"
                                        style={styles.rowIcon}
                                      />{" "}
                                      ACH Discount:
                                    </Text>
                                    <Text
                                      style={[
                                        styles.transactionValue,
                                        styles.discountValue
                                      ]}
                                    >
                                      -
                                      {formatCurrency(
                                        EXAMPLE_AMOUNTS[selectedExampleIndex]
                                          .value *
                                          (parseFloat(achDiscountPercent) / 100)
                                      )}
                                    </Text>
                                  </View>
                                )}

                              {/* Customer Total Amount - show the selected payment method prominently */}
                              <View style={styles.transactionRow}>
                                <Text style={styles.transactionLabel}>
                                  Customer Pays:
                                </Text>
                                <Text style={styles.transactionValue}>
                                  {formatCurrency(exampleCustomerPays)}
                                </Text>
                              </View>

                              <View style={styles.transactionRow}>
                                <Text style={styles.transactionLabel}>
                                  Processing Cost:
                                </Text>
                                <Text
                                  style={[
                                    styles.transactionValue,
                                    styles.processingCostValue
                                  ]}
                                >
                                  {centsToDisplayDollars(
                                    transactionFees[exampleCustomerPays]
                                      ?.totalFee?.[selectedPaymentMethod] || 0
                                  )}
                                </Text>
                              </View>

                              <View
                                style={[
                                  styles.transactionRow,
                                  styles.schoolReceivesRow
                                ]}
                              >
                                <Text style={styles.transactionLabel}>
                                  School Receives:
                                </Text>
                                <Text
                                  style={[
                                    styles.transactionValue,
                                    styles.schoolReceivesValue
                                  ]}
                                >
                                  {formatCurrency(
                                    calculateSchoolAmount(
                                      EXAMPLE_AMOUNTS[selectedExampleIndex]
                                        .value,
                                      selectedPaymentMethod,
                                      transactionFees[
                                        EXAMPLE_AMOUNTS[selectedExampleIndex]
                                          .value
                                      ]?.totalFee?.[selectedPaymentMethod] /
                                        100 || 0
                                    )
                                  )}
                                </Text>
                              </View>
                              <Text style={styles.feeNoteText}>
                                Note: If using Stripe's Invoicing/Billing
                                features, Stripe may charge your school an extra
                                0.4% or 0.7%.
                              </Text>
                            </>
                          )}
                        </>
                      )}
                    </View>
                  </>
                )}
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
      {!!accountLinkURL && (
        <BottomSheetModal
          onClose={() => setAccountLinkURL(null)}
          url={accountLinkURL}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  pageContent: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#f8f9fa"
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center"
  },
  scrollView: {
    flex: 1,
    width
  },
  contentContainer: {
    padding: 24,
    paddingBottom: 60
  },
  contentContainerStarting: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 60
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16
  },
  iconSuccess: {
    marginRight: 12
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: "600",
    color: "#222"
  },
  heroImage: {
    resizeMode: "contain",
    alignSelf: "center",
    zIndex: 999,
    marginBottom: 24
  },
  heading: {
    fontSize: 24,
    fontWeight: "700",
    marginBottom: 16,
    color: "#222"
  },
  bodyText: {
    fontSize: 16,
    color: "#333",
    marginBottom: 16
  },
  subheadingText: {
    fontSize: 16,
    lineHeight: 24,
    color: "#333",
    marginBottom: 16
  },
  button: {
    marginBottom: 4
  },
  primaryButton: {
    marginBottom: 4,
    height: 64,
    width: "100%"
  },
  primaryButtonText: {
    fontWeight: "600"
  },
  secondaryButton: {
    marginBottom: 32
  },
  stripeDashboardButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: primaryColor, // "#635BFF",
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 20,
    marginVertical: 20
  },
  stripeDashboardButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
    marginRight: 8
  },
  card: {
    backgroundColor: "white",
    borderRadius: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 3,
    marginBottom: 24,
    overflow: "hidden"
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0"
  },
  cardHeaderIcon: {
    marginRight: 12
  },
  cardHeaderTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#222"
  },
  cardBody: {
    padding: 20
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 12
  },
  infoLabel: {
    width: 80,
    fontWeight: "600",
    color: "#555"
  },
  infoValue: {
    flex: 1,
    color: "#333"
  },
  cardAction: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#f0f0f0"
  },
  cardActionText: {
    color: Glob.get("onespotColor") || "#6851ff",
    fontWeight: "600",
    marginRight: 8
  },
  feeSection: {
    marginBottom: 24,
    borderWidth: 1,
    borderColor: "#f0f0f0",
    borderRadius: 12,
    overflow: "hidden"
  },
  feeSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fafafa",
    paddingHorizontal: 16,
    paddingVertical: 12
  },
  feeSectionTitleContainer: {
    flexDirection: "row",
    alignItems: "center"
  },
  feeSectionIcon: {
    marginRight: 8
  },
  feeSectionTitle: {
    fontWeight: "600",
    fontSize: 16,
    color: "#333"
  },
  feeInputContainer: {
    padding: 16
  },
  feeInputRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12
  },
  feeInputLabel: {
    flex: 1,
    fontSize: 15,
    color: "#444"
  },
  percentageInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    width: 80,
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    paddingHorizontal: 10
  },
  percentageInput: {
    flex: 1,
    width: "100%",
    height: 40,
    textAlign: "right",
    paddingRight: 4
  },
  percentageSymbol: {
    marginLeft: 2,
    color: "#555"
  },
  thresholdInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    width: 100,
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    paddingHorizontal: 10
  },
  currencySymbol: {
    marginRight: 4,
    color: "#555"
  },
  thresholdInput: {
    flex: 1,
    height: 40
  },
  feeDescription: {
    fontSize: 13,
    color: "#666",
    fontStyle: "italic",
    marginTop: 8
  },
  exampleSection: {
    marginTop: 8,
    marginBottom: 24,
    backgroundColor: "#f8f9ff",
    borderRadius: 12,
    padding: 16
  },
  exampleHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 0,
    paddingVertical: 12,
    justifyContent: "space-between"
  },
  exampleHeaderExpanded: {
    marginBottom: 16
  },
  headerTextContainer: {
    flex: 1,
    marginLeft: 8
  },
  expandCollapseIcon: {
    marginLeft: 8
  },
  exampleHeaderText: {
    fontWeight: "600",
    color: "#333",
    fontSize: 16
  },
  exampleHeaderSubtext: {
    fontSize: 13,
    color: "#777",
    marginTop: 2
  },
  feeMethodSection: {
    marginBottom: 16,
    backgroundColor: "#fafafa",
    borderRadius: 8,
    padding: 12
  },
  feeMethodHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingRight: 4
  },
  feeMethodHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10
  },
  feeMethodIcon: {
    marginRight: 8
  },
  feeMethodTitle: {
    fontWeight: "600",
    fontSize: 15,
    color: "#444"
  },
  platformFeeRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    paddingHorizontal: 4
  },
  platformFeeLabel: {
    color: "#555"
  },
  platformFeeValue: {
    color: "#333"
  },
  platformFeeLink: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
    marginBottom: 24
  },
  platformFeeLinkText: {
    color: Glob.get("onespotColor") || "#6851ff",
    marginRight: 6
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Glob.get("onespotColor") || "#6851ff",
    borderRadius: 8,
    padding: 16,
    margin: 20,
    marginTop: 0
  },
  saveButtonDisabled: {
    opacity: 0.6
  },
  saveButtonText: {
    color: "white",
    fontWeight: "600",
    marginLeft: 8
  },
  loadingIndicator: {
    marginVertical: 20
  },
  compactFeeInfoContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    paddingTop: 24,
    paddingBottom: 12,
    borderTopWidth: 1,
    borderTopColor: "#f0f0f0"
  },
  compactFeeIcon: {
    marginRight: 12
  },
  compactFeeTextContainer: {
    flex: 1
  },
  compactFeeTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    marginBottom: 2
  },
  compactFeeDescription: {
    fontSize: 14,
    color: "#666"
  },
  setupFeeButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Glob.get("onespotColor") || "#6851ff",
    borderRadius: 8,
    padding: 14,
    margin: 16,
    marginTop: 0
  },
  setupFeeButtonText: {
    color: "white",
    fontWeight: "600",
    marginLeft: 8
  },
  exampleSelectorContainer: {
    marginVertical: 20
  },
  exampleSelectorLabel: {
    fontSize: 15,
    fontWeight: "500",
    color: "#444",
    marginBottom: 12
  },
  exampleSelector: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#f8f8f8",
    borderRadius: 12,
    padding: 8
  },
  exampleButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    paddingHorizontal: 8,
    borderRadius: 8,
    marginHorizontal: 4,
    backgroundColor: "white",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1
  },
  exampleButtonSelected: {
    backgroundColor: `${primaryColor}10`,
    borderColor: primaryColor,
    borderWidth: 1
  },
  exampleButtonIcon: {
    marginBottom: 8
  },
  exampleButtonText: {
    fontWeight: "700",
    fontSize: 16,
    color: "#444",
    marginBottom: 2
  },
  exampleButtonTextSelected: {
    color: primaryColor
  },
  exampleButtonSubtext: {
    fontSize: 12,
    color: "#777"
  },
  exampleButtonSubtextSelected: {
    color: primaryColor
  },
  transactionDetails: {
    marginTop: 8,
    padding: 20,
    backgroundColor: "white",
    borderRadius: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 3
  },
  transactionRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#f5f5f5"
  },
  rowIcon: {
    marginRight: 8
  },
  processingCostValue: {
    color: "#e74c3c"
  },
  schoolReceivesRow: {
    backgroundColor: "#f9f9f9",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginTop: 4,
    borderBottomWidth: 0
  },
  schoolReceivesValue: {
    color: "#27ae60",
    fontSize: 18
  },
  transactionLabel: {
    fontSize: 15,
    fontWeight: "500",
    color: "#555",
    flex: 3
  },
  transactionValue: {
    fontSize: 16,
    fontWeight: "700",
    color: "#333",
    textAlign: "right",
    flex: 2
  },
  feeLoader: {
    marginVertical: 24
  },
  discountRow: {
    backgroundColor: "#f1fff5",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginTop: 4,
    marginBottom: 8,
    borderBottomWidth: 0
  },
  discountValue: {
    color: "#27ae60",
    fontWeight: "700"
  },
  serviceFeeRow: {
    backgroundColor: "#fef8f3",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginTop: 4,
    marginBottom: 8,
    borderBottomWidth: 0
  },
  serviceFeeValue: {
    color: "#e67e22",
    fontWeight: "700"
  },
  paymentMethodContainer: {
    marginBottom: 20
  },
  paymentMethodToggle: {
    flexDirection: "row",
    backgroundColor: "#f8f8f8",
    borderRadius: 12,
    padding: 4
  },
  paymentMethodButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    margin: 4,
    backgroundColor: "white"
  },
  paymentMethodButtonSelected: {
    backgroundColor: `${primaryColor}10`,
    borderColor: primaryColor,
    borderWidth: 1
  },
  paymentMethodButtonIcon: {
    marginRight: 8
  },
  paymentMethodButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#555"
  },
  paymentMethodButtonTextSelected: {
    color: primaryColor
  },
  feeNoteText: {
    fontSize: 13,
    color: "#666",
    fontStyle: "italic",
    marginTop: 4
  }
});
